const PAD_W = 0.7;
const PAD_H = 0.8;

const normalizeAngle = (a: number) => ((a % 360) + 360) % 360;

function sizeForUpright(bounds: { width: number; height: number }, ratio: number) {
    const maxW = bounds.width * PAD_W;
    const maxH = bounds.height * PAD_H;
    const w = Math.min(maxW, maxH * ratio);
    const h = w / ratio;
    return { w, h };
}

function sizeForQuarterTurn(bounds: { width: number; height: number }, ratio: number) {
    // при повороте 90/270 ширина/высота меняются местами
    const maxH_afterRotation = bounds.width * PAD_W; // будет "height" после поворота
    const maxW_afterRotation = bounds.height * PAD_H; // будет "width" после поворота
    const H = Math.min(maxH_afterRotation, maxW_afterRotation / ratio);
    const W = ratio * H;
    return { w: W, h: H }; // размеры ДО поворота (чтобы затем повернуть)
}

/** Получаем bbox shape в координатах relativeTo (надежно через getScreenCTM) */
function getBBoxInScreenSpace(el: SVGGraphicsElement, relativeTo: SVGGraphicsElement) {
    try {
        const bbox = el.getBBox();
        const ctmFrom = el.getScreenCTM();
        const ctmTo = relativeTo.getScreenCTM();
        if (!ctmFrom || !ctmTo) return null;

        const m = ctmTo.inverse().multiply(ctmFrom);
        const corners = [
            new DOMPoint(bbox.x, bbox.y),
            new DOMPoint(bbox.x + bbox.width, bbox.y),
            new DOMPoint(bbox.x, bbox.y + bbox.height),
            new DOMPoint(bbox.x + bbox.width, bbox.y + bbox.height),
        ].map((p) => p.matrixTransform(m));

        const xs = corners.map((p) => p.x);
        const ys = corners.map((p) => p.y);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        const width = Math.max(...xs) - x;
        const height = Math.max(...ys) - y;
        return { x, y, width, height };
    } catch (err) {
        console.warn('getBBoxInScreenSpace error', err);
        return null;
    }
}

/** Парсим угол поворота: сначала data-rotate, затем transform="rotate(...)" */
function parseAngleFromGroup(g: SVGGElement) {
    const data = g.getAttribute('data-rotate');
    if (data) {
        const n = parseFloat(data);
        if (!Number.isNaN(n)) return normalizeAngle(n);
    }
    const tr = g.getAttribute('transform') || '';
    const m = tr.match(/rotate\(\s*(-?\d+(\.\d+)?)/i);
    if (m) return normalizeAngle(parseFloat(m[1]));
    return 0;
}

export default function setImageBookingRooms(bookedRooms: any[]) {
    bookedRooms.forEach((room: any) => {
        if (!room) return;
        const { element, standData } = room;
        const group = element as SVGGElement;
        if (!group) return;

        // уже есть логотип — пропускаем
        if (group.querySelector('image')) return;
        if (!standData?.booking?.company) return;

        let { width, height, src } = standData.booking.company.previewLogo || standData.booking.company.logo || {};
        if (!width || !height) {
            width = 160;
            height = 160 / (16 / 9);
        }
        const ratio = width / height;

        const shape = group.querySelector<SVGGraphicsElement>('path, rect, polygon, circle, ellipse');
        if (!shape) {
            return;
        }

        // !!! важно: функция должна выполняться после того, как SVG уже в DOM и отрисован
        const bounds = getBBoxInScreenSpace(shape, group);
        if (!bounds || !bounds.width || !bounds.height) {
            return;
        }

        const angle = parseAngleFromGroup(group);
        const isQuarter = angle === 90 || angle === 270;

        const { w, h } = isQuarter ? sizeForQuarterTurn(bounds, ratio) : sizeForUpright(bounds, ratio);
        const cx = bounds.x + bounds.width / 2;
        const cy = bounds.y + bounds.height / 2;
        const x = cx - w / 2;
        const y = cy - h / 2;

        const svgNS = 'http://www.w3.org/2000/svg';
        const XLINK = 'http://www.w3.org/1999/xlink';
        const image = document.createElementNS(svgNS, 'image');

        image.setAttribute('x', String(x));
        image.setAttribute('y', String(y));
        image.setAttribute('width', String(w));
        image.setAttribute('height', String(h));
        // для старых браузеров и современных — ставим оба
        if (src) {
            image.setAttribute('href', src);
            image.setAttributeNS(XLINK, 'xlink:href', src);
        } else {
            image.setAttribute('href', '');
        }
        image.setAttribute('preserveAspectRatio', 'xMidYMid slice');
        image.setAttribute('pointer-events', 'none');
        image.classList.add('booking-logo');

        // Если группа уже содержит rotate в transform — НЕ вращаем image ещё раз.
        const groupHasRotate = !!(group.getAttribute('transform') || '').match(/rotate\(/i);
        if (!groupHasRotate && angle !== 0) {
            // поворачиваем относительно центра (в координатах group)
            image.setAttribute('transform', `rotate(${angle} ${cx} ${cy})`);
        }

        group.appendChild(image);
    });
}
